import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../../utils/fetchInterceptor';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

interface ContentSection {
  id: string;
  title: string;
  type: 'hero' | 'features' | 'pricing' | 'cta' | 'video';
  isActive: boolean;
  lastModified: string;
}

interface HeroContent {
  title: string;
  subtitle: string;
  description: string;
  primaryButton: string;
  secondaryButton: string;
  backgroundVideo: string;
  overlayOpacity: number;
}

interface FeatureContent {
  title: string;
  description: string;
  features: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
}

const Content: React.FC = () => {
  const [activeTab, setActiveTab] = useState('hero');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [heroContent, setHeroContent] = useState<HeroContent>({
    title: "HIGH CODE",
    subtitle: "STEM Adventures for Young Innovators!",
    description: "Join our amazing journey into the world of electronics, coding, and robotics! Build 50+ cool projects with Arduino, Raspberry Pi, ESP boards, and more!",
    primaryButton: "Start Building Now!",
    secondaryButton: "View Projects",
    backgroundVideo: "/videos/kids_hero_section.mp4",
    overlayOpacity: 60
  });

  const [featureContent, setFeatureContent] = useState<FeatureContent>({
    title: "Why Choose Code Coast?",
    description: "Discover what makes our STEM education platform unique",
    features: [
      {
        icon: "lightbulb",
        title: "Engaging Projects",
        description: "Over 50+ exciting projects using Arduino, Raspberry Pi, and ESP boards."
      },
      {
        icon: "chalkboard-teacher",
        title: "Hands-On Learning",
        description: "Develop practical skills in electronics, programming, and robotics."
      },
      {
        icon: "star",
        title: "Future-Ready Skills",
        description: "Prepare for a future in tech with foundational STEM knowledge."
      }
    ]
  });

  const [contentSections, setContentSections] = useState<ContentSection[]>([]);

  // Fetch content from API
  const fetchContent = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/content`
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch content");

      const data = await response.json();
      
      // Set content sections
      setContentSections(data.sections || [
        { id: 'hero', title: 'Hero Section', type: 'hero', isActive: true, lastModified: '2024-01-15' },
        { id: 'features', title: 'Features Section', type: 'features', isActive: true, lastModified: '2024-01-15' },
        { id: 'age-groups', title: 'Age Groups', type: 'features', isActive: true, lastModified: '2024-01-15' },
        { id: 'starter-packs', title: 'Starter Packs', type: 'pricing', isActive: true, lastModified: '2024-01-15' },
        { id: 'projects', title: 'Projects Showcase', type: 'features', isActive: true, lastModified: '2024-01-15' },
        { id: 'learning-path', title: 'Learning Journey', type: 'features', isActive: true, lastModified: '2024-01-15' },
        { id: 'cta', title: 'Call to Action', type: 'cta', isActive: true, lastModified: '2024-01-15' }
      ]);
      
      // Set hero content if available
      if (data.heroContent) {
        setHeroContent(data.heroContent);
      }
      
      // Set feature content if available
      if (data.featureContent) {
        setFeatureContent(data.featureContent);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      console.error("Error fetching content:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const tabs = [
    { id: 'hero', label: 'Hero Section', icon: 'home' },
    { id: 'features', label: 'Features', icon: 'star' },
    { id: 'age-groups', label: 'Age Groups', icon: 'users' },
    { id: 'starter-packs', label: 'Starter Packs', icon: 'box' },
    { id: 'projects', label: 'Projects', icon: 'code' },
    { id: 'learning-path', label: 'Learning Path', icon: 'route' },
    { id: 'cta', label: 'Call to Action', icon: 'bullhorn' },
    { id: 'video', label: 'Video Settings', icon: 'video' }
  ];

  const saveHeroContent = async () => {
    try {
      setIsSaving(true);
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/content/hero`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(heroContent),
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to save hero content");

      alert('Hero content saved successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save hero content");
      console.error("Error saving hero content:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const saveFeatureContent = async () => {
    try {
      setIsSaving(true);
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/content/features`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(featureContent),
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to save feature content");

      alert('Feature content saved successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save feature content");
      console.error("Error saving feature content:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const previewContent = () => {
    window.open('/high-code', '_blank');
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Content</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchContent}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Content Management</h1>
          <p className="text-gray-600">Manage High Code page content and settings</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={previewContent}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            <i className="fas fa-eye mr-2"></i>
            Preview Page
          </button>
          <button 
            disabled={isSaving}
            className={`bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <i className="fas fa-save mr-2"></i>
            {isSaving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>

      {/* Content Sections Overview */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Content Sections</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contentSections.map((section) => (
            <div key={section.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-gray-900">{section.title}</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  section.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {section.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-sm text-gray-500">Last modified: {section.lastModified}</p>
              <button
                onClick={() => setActiveTab(section.id)}
                className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Edit Section →
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`fas fa-${tab.icon} mr-2`}></i>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'hero' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Hero Section Settings</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Main Title</label>
                  <input
                    type="text"
                    value={heroContent.title}
                    onChange={(e) => setHeroContent({...heroContent, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                  <input
                    type="text"
                    value={heroContent.subtitle}
                    onChange={(e) => setHeroContent({...heroContent, subtitle: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={heroContent.description}
                  onChange={(e) => setHeroContent({...heroContent, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Primary Button Text</label>
                  <input
                    type="text"
                    value={heroContent.primaryButton}
                    onChange={(e) => setHeroContent({...heroContent, primaryButton: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Button Text</label>
                  <input
                    type="text"
                    value={heroContent.secondaryButton}
                    onChange={(e) => setHeroContent({...heroContent, secondaryButton: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Background Video URL</label>
                  <input
                    type="text"
                    value={heroContent.backgroundVideo}
                    onChange={(e) => setHeroContent({...heroContent, backgroundVideo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Overlay Opacity (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={heroContent.overlayOpacity}
                    onChange={(e) => setHeroContent({...heroContent, overlayOpacity: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={saveHeroContent}
                  disabled={isSaving}
                  className={`bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSaving ? 'Saving...' : 'Save Hero Settings'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Features Section Settings</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                <input
                  type="text"
                  value={featureContent.title}
                  onChange={(e) => setFeatureContent({...featureContent, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
                <textarea
                  value={featureContent.description}
                  onChange={(e) => setFeatureContent({...featureContent, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Feature Items</h4>
                {featureContent.features.map((feature, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                        <input
                          type="text"
                          value={feature.icon}
                          onChange={(e) => {
                            const newFeatures = [...featureContent.features];
                            newFeatures[index].icon = e.target.value;
                            setFeatureContent({...featureContent, features: newFeatures});
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                        <input
                          type="text"
                          value={feature.title}
                          onChange={(e) => {
                            const newFeatures = [...featureContent.features];
                            newFeatures[index].title = e.target.value;
                            setFeatureContent({...featureContent, features: newFeatures});
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <input
                          type="text"
                          value={feature.description}
                          onChange={(e) => {
                            const newFeatures = [...featureContent.features];
                            newFeatures[index].description = e.target.value;
                            setFeatureContent({...featureContent, features: newFeatures});
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={saveFeatureContent}
                  disabled={isSaving}
                  className={`bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSaving ? 'Saving...' : 'Save Features Settings'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'video' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Video Settings</h3>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <i className="fas fa-exclamation-triangle text-yellow-600 mr-3 mt-1"></i>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Video Management</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Video files are managed separately. Use the file upload system to replace background videos.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Background Video</label>
                  <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-600">{heroContent.backgroundVideo}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Upload New Video</label>
                  <input
                    type="file"
                    accept="video/*"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Video Autoplay</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="true">Enabled</option>
                    <option value="false">Disabled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Video Loop</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="true">Enabled</option>
                    <option value="false">Disabled</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Other tabs would have similar content management interfaces */}
          {!['hero', 'features', 'video'].includes(activeTab) && (
            <div className="text-center py-12">
              <i className="fas fa-cog text-4xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Content Management</h3>
              <p className="text-gray-600">
                This section is under development. Content management for {activeTab} will be available soon.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Content;
